import { DynamoDBClient } from '@aws-sdk/client-dynamodb';
import { DynamoDBDocumentClient, GetCommand, ScanCommand } from '@aws-sdk/lib-dynamodb';
import { awsCredentialsService } from './awsCredentialsService';
import { getConfigValue } from './configService';
import { portalAdmin } from './portalAdmin';

function getRegion(): string {
  return getConfigValue('AWS_REGION') || 'us-east-1';
}

export type RepositoryName = 'numa-deploy' | 'numa-deploy-dev';

export interface ImageMetadata {
  repository: RepositoryName;
  imageTag: string;
  digest: string;
  customName?: string;
  description?: string;
  createdAt?: string;
  createdBy?: string;
}

// The DDB table's hash key is `imageTag` (legacy field name) — we encode the
// repository as a prefix so the same SHA tag in numa-deploy vs numa-deploy-dev
// gets distinct rows (and distinct custom names). Format: `${repository}#${imageTag}`.
function encodeKey(repository: RepositoryName, imageTag: string): string {
  return `${repository}#${imageTag}`;
}

// Pre-migration rows have no prefix (just the bare tag). Used as a fallback during
// the transition window so renamed prod images keep showing their custom names
// before the backfill runs.
function legacyKey(imageTag: string): string {
  return imageTag;
}

function decodeKey(encoded: string): { repository: RepositoryName; imageTag: string } | null {
  const sepIdx = encoded.indexOf('#');
  if (sepIdx === -1) return null; // legacy/un-prefixed row
  const repo = encoded.slice(0, sepIdx);
  if (repo !== 'numa-deploy' && repo !== 'numa-deploy-dev') return null;
  return { repository: repo, imageTag: encoded.slice(sepIdx + 1) };
}

function getDdbDoc(): DynamoDBDocumentClient {
  const region = getRegion();
  const client = awsCredentialsService.withRevokedRetry(
    new DynamoDBClient({ region, credentials: awsCredentialsService.credentials() })
  );
  return DynamoDBDocumentClient.from(client);
}

function getImageMetadataTable(): string {
  return getConfigValue('IMAGE_METADATA_TABLE') || 'numa-portal-image-metadata';
}

interface RawRow {
  imageTag: string; // DDB hash key — encoded as `${repository}#${realTag}` post-migration
  digest: string;
  repository?: RepositoryName; // present on migrated/new rows
  customName?: string;
  description?: string;
  createdAt?: string;
  createdBy?: string;
}

function rowToMetadata(row: RawRow): ImageMetadata | null {
  const decoded = decodeKey(row.imageTag);
  if (decoded) {
    return {
      repository: decoded.repository,
      imageTag: decoded.imageTag,
      digest: row.digest,
      customName: row.customName,
      description: row.description,
      createdAt: row.createdAt,
      createdBy: row.createdBy,
    };
  }
  // Legacy row (no prefix) — assume prod (numa-deploy). The migration script
  // backfills these rows to the new format so this branch goes away over time.
  return {
    repository: 'numa-deploy',
    imageTag: row.imageTag,
    digest: row.digest,
    customName: row.customName,
    description: row.description,
    createdAt: row.createdAt,
    createdBy: row.createdBy,
  };
}

export async function getImageMetadata(
  repository: RepositoryName,
  imageTag: string,
  digest: string
): Promise<ImageMetadata | null> {
  const table = getImageMetadataTable();
  const ddb = getDdbDoc();

  try {
    // Try the prefixed key first
    const res = await ddb.send(
      new GetCommand({
        TableName: table,
        Key: { imageTag: encodeKey(repository, imageTag), digest },
      })
    );
    if (res.Item) return rowToMetadata(res.Item as RawRow);

    // Fallback: legacy un-prefixed row, only meaningful for prod
    if (repository === 'numa-deploy') {
      const legacy = await ddb.send(
        new GetCommand({
          TableName: table,
          Key: { imageTag: legacyKey(imageTag), digest },
        })
      );
      if (legacy.Item) return rowToMetadata(legacy.Item as RawRow);
    }
    return null;
  } catch (error) {
    console.warn('Failed to get image metadata:', error);
    return null;
  }
}

/**
 * Save the name and description of one image, through the portal-admin
 * Lambda (TASK-630). The server records the person from the sign-in.
 */
export async function setImageMetadata(metadata: ImageMetadata): Promise<void> {
  try {
    await portalAdmin('images.setMetadata', {
      repository: metadata.repository,
      imageTag: metadata.imageTag,
      digest: metadata.digest,
      ...(metadata.customName ? { customName: metadata.customName } : {}),
      ...(metadata.description ? { description: metadata.description } : {}),
    });
  } catch (error) {
    console.error('Failed to set image metadata:', error);
    throw new Error(`Unable to save image metadata.${error instanceof Error ? ` ${error.message}` : ''}`);
  }
}

export async function deleteImageMetadata(repository: RepositoryName, imageTag: string, digest: string): Promise<void> {
  try {
    await portalAdmin('images.deleteMetadata', { repository, imageTag, digest });
  } catch (error) {
    console.error('Failed to delete image metadata:', error);
    throw new Error(`Unable to delete image metadata.${error instanceof Error ? ` ${error.message}` : ''}`);
  }
}

export async function getAllImageMetadata(): Promise<ImageMetadata[]> {
  const table = getImageMetadataTable();
  const ddb = getDdbDoc();

  try {
    const res = await ddb.send(new ScanCommand({ TableName: table, Limit: 500 }));
    const rows = (res.Items as RawRow[]) || [];
    return rows.map(rowToMetadata).filter((m): m is ImageMetadata => m !== null);
  } catch (error) {
    console.warn('Failed to get all image metadata:', error);
    return [];
  }
}

export function getDisplayName(imageTag: string, customName?: string): string {
  return customName || imageTag;
}
